import mustache from "mustache";
import { ClienteFincaLoteResponse } from "../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { AnalisisLaboratorioResponse } from "../../../../modulos/fertilizacion/response/AnalisisLaboratorioResponse";
import { AnalisisRegistroResponse } from "../../../../modulos/fertilizacion/response/AnalisisRegistroResponse";
import { AsignarLotesRequest } from "../../../../modulos/fertilizacion/request/AsignarLotesRequest";
import { formatFechaCorta } from "../../../../utils/fechaUtils";
import { decodeFileName } from "../../../../utils/stringUtils";
import { IFormBodyRender } from "../../../definitions/form/IFormBodyRender";
import { LotePickerModalRender } from "../../terceros/lote/modal/LotePickerModalRender";
import template from "./fertilizacionValidacionFormTemplate.html";

mustache.parse(template);

/** Contador para ids únicos por instancia. */
let FERTI_VALIDA_ID = 0;

/** Cuerpo del modal de registro de lotes (Render/Connect): banner + tabla con,
 *  por registro, un `<select>` de lotes + un botón para elegir el lote sobre el
 *  mapa (abre `LotePickerModalRender`). Select y mapa quedan sincronizados
 *  (ambos escriben el mismo estado). Escribe `request.asignaciones` con los
 *  registros cuyo lote cambió; `validate()` exige que todos tengan lote. Todos
 *  los registros son editables (decisión de diseño); los botones editar/eliminar
 *  se ubican más adelante. */
export class FertilizacionValidacionFormRender implements IFormBodyRender {
  /** Id único de la instancia (para los ids del DOM). */
  private readonly _id: number;
  /** Lote elegido por registroId (inicia con el matcheado, o "" si pendiente). */
  private readonly _selected = new Map<string, string>();

  /**
   * @param _request  Request de asignación que este form va poblando.
   * @param _analisis Análisis con los registros a resolver.
   * @param _lotes    Lotes de la finca (opciones del selector).
   */
  constructor(
    private readonly _request: AsignarLotesRequest,
    private readonly _analisis: AnalisisLaboratorioResponse,
    private readonly _lotes: ClienteFincaLoteResponse[],
  ) {
    FERTI_VALIDA_ID += 1;
    this._id = FERTI_VALIDA_ID;
    for (const registro of this._analisis.registros) {
      this._selected.set(registro.id, registro.loteId ?? "");
    }
  }

  render(): string {
    const registros = this._analisis.registros;
    const sinAsignar = registros.filter((registro) => !registro.loteId).length;
    return mustache.render(template, {
      id: this._id,
      archivo: decodeFileName(this._analisis.nombreArchivo),
      fecha: formatFechaCorta(this._analisis.fechaAnalisis),
      totalRegistros: `${this._analisis.totalRegistros} registros`,
      conLote: registros.length - sinAsignar,
      sinAsignar,
      // El server genera la recomendación dentro de `asignar-lotes`: si todavía
      // no hay, avisa que guardar es lo que la dispara.
      sinDiagnostico: this._analisis.recomendacionFecha == null,
      rows: registros.map((registro) => ({
        idMuestra: registro.idMuestra,
        loteExcel: registro.loteExcel,
        crop: registro.crop ?? registro.cropRaw ?? "—",
        loteCell: this._buildLoteCell(registro),
      })),
    });
  }

  connect(): void {
    this._errorEl = document.getElementById(`ferti-valida-error-${this._id}`);
    this._tbody = document.getElementById(`ferti-valida-tbody-${this._id}`);
    this._tbody?.addEventListener("change", this._onChange);
    this._tbody?.addEventListener("click", this._onClick);
  }

  dispose(): void {
    this._tbody?.removeEventListener("change", this._onChange);
    this._tbody?.removeEventListener("click", this._onClick);
  }

  /** Todos los registros deben tener lote; si falta alguno, muestra el error. */
  validate(): boolean {
    const faltan = this._analisis.registros.filter(
      (registro) => !this._selected.get(registro.id),
    );
    if (faltan.length > 0) {
      if (this._errorEl) {
        this._errorEl.textContent = `Asigná un lote a los ${faltan.length} registro(s) sin asignar.`;
        this._errorEl.style.display = "";
      }
      return false;
    }
    this._syncRequest();
    return true;
  }

  private _errorEl: HTMLElement | null = null;
  private _tbody: HTMLElement | null = null;

  /** Cambio del `<select>`: actualiza el lote elegido del registro. */
  private _onChange = (event: Event): void => {
    const select = event.target as HTMLSelectElement;
    const registroId = select.getAttribute("data-registro-id");
    if (!registroId) return;
    this._setLote(registroId, select.value);
  };

  /** Click en el botón de mapa de una fila: abre el picker de lote. */
  private _onClick = (event: Event): void => {
    const btn = (event.target as HTMLElement).closest<HTMLElement>(
      '[data-act="map"]',
    );
    const registroId = btn?.getAttribute("data-registro-id");
    if (!registroId) return;
    this._abrirPicker(registroId);
  };

  /** Abre el picker de lote sobre el mapa para el registro dado (resalta el ya
   *  elegido); al pickear, asigna ese lote a la fila. */
  private _abrirPicker(registroId: string): void {
    const registro = this._analisis.registros.find((r) => r.id === registroId);
    if (!registro) return;
    new LotePickerModalRender(
      `Lote para ${registro.loteExcel}`,
      (lote) => this._asignarDesdeMapa(registroId, lote),
      this._selected.get(registroId) || undefined,
    ).init();
  }

  /** Asigna el lote elegido en el mapa y refleja la selección en el `<select>`. */
  private _asignarDesdeMapa(
    registroId: string,
    lote: ClienteFincaLoteResponse,
  ): void {
    const select = this._tbody?.querySelector<HTMLSelectElement>(
      `select[data-registro-id="${registroId}"]`,
    );
    if (select) select.value = lote.id;
    this._setLote(registroId, lote.id);
  }

  /** Fija el lote elegido de un registro (desde el select o el mapa), re-sincroniza
   *  el request y limpia el error si ya hay valor. */
  private _setLote(registroId: string, loteId: string): void {
    this._selected.set(registroId, loteId);
    this._syncRequest();
    if (this._errorEl && loteId) this._errorEl.style.display = "none";
  }

  /** Reconstruye `request.asignaciones` con los registros cuyo lote elegido
   *  difiere del original (evita re-marcar como `manual` los que no cambiaron). */
  private _syncRequest(): void {
    this._request.asignaciones = this._analisis.registros
      .filter((registro) => {
        const elegido = this._selected.get(registro.id) ?? "";
        return elegido !== "" && elegido !== (registro.loteId ?? "");
      })
      .map((registro) => ({
        registroId: registro.id,
        loteId: this._selected.get(registro.id)!,
      }));
  }

  /** Celda "Lote en sistema": `<select>` de lotes (pre-seleccionado con el
   *  matcheado) + botón para elegir sobre el mapa. */
  private _buildLoteCell(registro: AnalisisRegistroResponse): string {
    const options = this._lotes
      .map(
        (lote) =>
          `<option value="${lote.id}"${
            lote.id === registro.loteId ? " selected" : ""
          }>${lote.nombreLote}</option>`,
      )
      .join("");
    return `<div class="ferti-lote-cell">
      <select class="sx-input" data-registro-id="${registro.id}"><option value="">— Seleccionar lote —</option>${options}</select>
      <button type="button" class="sx-btn sx-btn-outline sx-btn-icon" data-act="map" data-registro-id="${registro.id}" title="Elegir en el mapa"><i class="ph ph-map-pin"></i></button>
    </div>`;
  }
}
